import { useMemo } from 'react';
import type { AcmBlock, AcmColumn, AcmRecord } from '../types/kpi.js';
import type { Granularity } from '../lib/kpi-aggregation.js';
import { aggregateRecords, GRANULARITY_OPTIONS } from '../lib/kpi-aggregation.js';
import { KpiBlockChart, type ChartDataPoint } from './KpiBlockChart.js';
import { KpiCvrChart } from './KpiCvrChart.js';
import { ChartFilterBar } from './chart/ChartFilterBar.js';

/** @graph-connects none */
const BLOCK_ORDER: AcmBlock[] = ['会員数', 'スタイリング', 'チケット・返却', '集客'];

interface KpiChartViewProps {
  columns: AcmColumn[];
  rows: AcmRecord[];
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次KPIのグラフ表示モード。粒度切り替え＋ブロックごとの棒グラフで推移を可視化する。
 * @graph-connects aggregateRecords [calls] 粒度集約
 * @graph-connects KpiBlockChart [calls] ブロック単位チャート
 * @graph-connects KpiCvrChart [calls] CVR推移チャート
 */
export function KpiChartView({
  columns,
  rows,
  granularity,
  onGranularityChange,
}: KpiChartViewProps) {
  const aggregated = useMemo(() => aggregateRecords(rows, granularity), [rows, granularity]);

  const chartData = useMemo(
    () =>
      aggregated.map((a) => {
        const point: ChartDataPoint = { label: a.label };
        for (const col of columns) {
          point[col.key] = a.record[col.key];
        }
        return point;
      }),
    [aggregated, columns],
  );

  const blockColumns = useMemo(() => {
    const map = new Map<AcmBlock, AcmColumn[]>();
    for (const col of columns) {
      const arr = map.get(col.block);
      if (arr) {
        arr.push(col);
      } else {
        map.set(col.block, [col]);
      }
    }
    return map;
  }, [columns]);

  return (
    <div className="mt-6 space-y-4">
      <ChartFilterBar
        label="粒度"
        options={GRANULARITY_OPTIONS}
        value={granularity}
        onChange={onGranularityChange}
      />

      {BLOCK_ORDER.map((block) => {
        const cols = blockColumns.get(block);
        if (!cols || cols.length === 0) return null;
        return <KpiBlockChart key={block} title={block} columns={cols} data={chartData} />;
      })}

      <KpiCvrChart aggregated={aggregated} />

      <p className="text-xs text-[var(--color-text-muted)]">
        {aggregated.length.toLocaleString('ja-JP')} 期間分
      </p>
    </div>
  );
}
